import IconCloud from "@common/components/icons/IconCloud";
import { darkNotificationProps } from "@common/constants/theme-mantine/styling";
import { showNotification } from "@mantine/notifications";
import { INSTANCE_RESTART_TIMEOUT } from "@stores/instances/constants";
import { IPurchase } from "@stores/purchases/types";
import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** Time the instance needs to come back after a plan change */
export { INSTANCE_RESTART_TIMEOUT };

interface IPlanChangeValue {
  /** a plan change is in flight somewhere under this provider */
  isChangingPlan: boolean;
  /** call while a mutation is pending; returns its unregister */
  registerPendingChange: () => () => void;
  /** the instance is coming back up after a successful change */
  isRestartingInstance: boolean;
  /** (re)starts the restart window — refreshes it if one is already open */
  startRestartWindow: () => void;
}

const PlanChangeContext = createContext<IPlanChangeValue | null>(null);

export interface IPlanChangeProviderProps
  extends PropsWithChildren,
    Pick<IPurchase, "hosted_instance"> {}

/**
 * Coordinates plan changes across everything it wraps.
 *
 * Two jobs, both of which only make sense shared: only one add-on may be
 * changed at a time, so every consumer sees the same in-flight state; and the
 * instance needs some time to come back afterwards, so we keep a single restart
 * window running and notify once it is over. Self-hosted instances do not
 * restart, so their window never opens.
 *
 * Renders no UI of its own — place an `<InstanceRestartBanner />` inside it
 * wherever the restart notice belongs.
 */
export function PlanChangeProvider({
  children,
  hosted_instance,
}: IPlanChangeProviderProps): ReactElement {
  const isCloud = !!hosted_instance;

  // Counted rather than a boolean so overlapping consumers — and any that
  // unmount mid-request — can't leave the lock stuck on.
  const [pendingChanges, setPendingChanges] = useState(0);

  const registerPendingChange = useCallback(() => {
    setPendingChanges((count) => count + 1);

    return () => setPendingChanges((count) => count - 1);
  }, []);

  const [isRestartingInstance, setIsRestartingInstance] = useState(false);
  const restartTimeout = useRef<ReturnType<typeof setTimeout>>();

  // A change that lands while the instance is already restarting restarts the
  // window from zero rather than inheriting the tail of the previous one.
  const startRestartWindow = useCallback(() => {
    if (!isCloud) {
      return;
    }

    clearTimeout(restartTimeout.current);
    setIsRestartingInstance(true);

    restartTimeout.current = setTimeout(() => {
      setIsRestartingInstance(false);

      showNotification({
        ...darkNotificationProps,
        icon: <IconCloud color={darkNotificationProps.iconcolor} size={18} />,
        autoClose: 5000,
        message: "Your instance is ready",
      });
    }, INSTANCE_RESTART_TIMEOUT);
  }, [isCloud]);

  useEffect(() => () => clearTimeout(restartTimeout.current), []);

  const isChangingPlan = pendingChanges > 0;

  const planChange = useMemo(
    () => ({
      isChangingPlan,
      registerPendingChange,
      isRestartingInstance,
      startRestartWindow,
    }),
    [
      isChangingPlan,
      registerPendingChange,
      isRestartingInstance,
      startRestartWindow,
    ],
  );

  return (
    <PlanChangeContext.Provider value={planChange}>
      {children}
    </PlanChangeContext.Provider>
  );
}

/**
 * Returns the plan-change state shared by the surrounding provider. Every
 * consumer under the same provider sees the same in-flight lock and the same
 * restart window, so one change locks all of them and shows one banner.
 */
export function usePlanChange(): IPlanChangeValue {
  const planChange = useContext(PlanChangeContext);

  if (!planChange) {
    throw new Error("usePlanChange must be used within a <PlanChangeProvider>");
  }

  return planChange;
}
